(() => {
  if (customElements.get('tkp-bundle-wines')) return;

  const query = `query TastingKitWines($id: ID!, $after: String) {
    node(id: $id) {
      ... on ProductVariant {
        id requiresComponents
        components(first: 30, after: $after) {
          nodes {
            quantity
            productVariant {
              id title
              product {
                id handle title vendor productType description
                featuredImage { url altText width height }
              }
            }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  }`;

  class TkpBundleWines extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.grid = this.querySelector('[data-wine-grid]');
      this.status = this.querySelector('[data-wine-status]');
      this.ids = this.dataset.variantIds.split(',');
      this.cardCache = new Map();
      this.querySelectorAll('[data-wine-id]').forEach(card => {
        this.cardCache.set(card.dataset.wineId, card.cloneNode(true));
      });
      document.addEventListener('VARIANT_CHANGE', event => {
        const id = String(event.target?.currentVariant?.id || '');
        if (this.ids.includes(id)) this.load(id);
      }, { capture: true, signal: this.events.signal });
      this.load(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.events = null;
    }

    async components(id, signal) {
      const nodes = [];
      let after = null;
      do {
        const response = await fetch(`${this.dataset.root}api/2026-10/graphql.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, variables: { id: `gid://shopify/ProductVariant/${id}`, after } }),
          signal
        });
        if (!response.ok) throw new Error('Bundle request failed');
        const result = await response.json();
        if (result.errors?.length || !result.data?.node) throw new Error('Bundle unavailable');
        if (!result.data.node.requiresComponents) return null;
        const connection = result.data.node.components;
        nodes.push(...connection.nodes);
        after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
      } while (after);
      return nodes;
    }

    groupWines(components) {
      const wines = new Map();
      components.forEach(({ quantity, productVariant }) => {
        const product = productVariant.product;
        const type = product.productType.toLowerCase();
        const variantTitle = productVariant.title.toLowerCase();
        const wine = ['tube', 'wine'].includes(type) || /100\s?ml|\btube\b/i.test(product.title + ' ' + variantTitle);
        if (!wine || quantity < 1) return;
        const id = product.id.split('/').pop();
        const existing = wines.get(id);
        if (existing) existing.quantity += quantity;
        else wines.set(id, { id, quantity, variant: productVariant, product });
      });
      return [...wines.values()];
    }

    async cardFor(wine, signal) {
      if (this.cardCache.has(wine.id)) return this.cardCache.get(wine.id).cloneNode(true);
      try {
        const url = new URL(`${this.dataset.root}products/${wine.product.handle}`, location.origin);
        url.searchParams.set('section_id', 'tkp-wine-card');
        url.searchParams.set('variant', wine.variant.id.split('/').pop());
        url.searchParams.set('preview_theme_id', this.dataset.themeId);
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error('Wine detail request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const card = html.querySelector(`[data-wine-id="${wine.id}"]`);
        if (!card) throw new Error('Wine card unavailable');
        card.querySelectorAll('script').forEach(script => script.remove());
        this.cardCache.set(wine.id, card.cloneNode(true));
        return card;
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return this.basicCard(wine);
      }
    }

    basicCard(wine) {
      const element = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        if (text) node.textContent = text;
        return node;
      };
      const card = element('article', 'tkp__wine');
      card.dataset.wineId = wine.id;
      card.dataset.wineHandle = wine.product.handle;
      const media = element('div', 'tkp__wine-media');
      const image = wine.product.featuredImage;
      if (image?.url && new URL(image.url).protocol === 'https:') {
        const img = document.createElement('img');
        const src = new URL(image.url);
        src.searchParams.set('width', '800');
        img.src = src.href;
        img.alt = image.altText || wine.product.title;
        img.loading = 'lazy';
        if (image.width && image.height) { img.width = image.width; img.height = image.height; }
        media.append(img);
      }
      const quantity = element('span', 'tkp__quantity');
      quantity.dataset.wineQuantity = '';
      media.append(quantity);
      const body = element('div', 'tkp__wine-body');
      body.append(element('p', 'tkp__producer', wine.product.vendor));
      body.append(element('h3', 'tkp__wine-title', wine.product.title));
      const copy = wine.product.description.split(/\s+/).slice(0, 22).join(' ');
      if (copy) body.append(element('p', 'tkp__wine-character', copy));
      const link = element('a', 'tkp__link', this.dataset.linkText);
      link.href = `${this.dataset.root}products/${wine.product.handle}?variant=${wine.variant.id.split('/').pop()}`;
      body.append(link);
      card.append(media, body);
      return card;
    }

    async load(id) {
      if (!id || id === this.currentVariant) return;
      const changed = this.currentVariant && this.currentVariant !== id;
      this.currentVariant = id;
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      if (changed) this.grid.replaceChildren();
      this.grid.setAttribute('aria-busy', 'true');
      this.status.hidden = this.grid.children.length > 0;
      this.status.textContent = this.dataset.loadingText;
      try {
        const components = await this.components(id, signal);
        if (components === null) return;
        const wines = this.groupWines(components);
        const cards = await Promise.all(wines.map(wine => this.cardFor(wine, signal)));
        if (signal.aborted || this.currentVariant !== id) return;
        cards.forEach((card, index) => {
          const wine = wines[index];
          card.dataset.componentId = wine.variant.id.split('/').pop();
          const quantity = card.querySelector('[data-wine-quantity]');
          if (quantity) quantity.textContent = this.dataset.quantityText.replace('COUNT', String(wine.quantity));
          card.querySelectorAll('a').forEach(link => {
            const url = new URL(link.href, location.origin);
            if (url.origin === location.origin && url.pathname.includes('/products/')) {
              url.searchParams.set('variant', wine.variant.id.split('/').pop());
              link.href = url.href;
            }
          });
        });
        this.grid.replaceChildren(...cards);
        this.status.textContent = wines.length ? '' : this.dataset.emptyText;
        this.status.hidden = wines.length > 0;
      } catch (error) {
        if (signal.aborted) return;
        this.currentVariant = null;
        this.status.textContent = this.dataset.emptyText;
        this.status.hidden = this.grid.children.length > 0;
      } finally {
        if (!signal.aborted) {
          this.grid.setAttribute('aria-busy', 'false');
          if (this.status.textContent === this.dataset.loadingText) {
            this.status.textContent = this.dataset.emptyText;
            this.status.hidden = this.grid.children.length > 0;
          }
        }
      }
    }
  }

  customElements.define('tkp-bundle-wines', TkpBundleWines);
})();
